import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

export type AddressFormValue = {
  address_line1: string;
  address_line2?: string;
  city: string;
  region?: string;
  postal_code?: string;
  country_code: string; // ISO-3166-1 alpha-2
  address_type: 'warehouse' | 'hub' | 'customer_site' | 'vendor' | 'other';
  latitude?: number | null;
  longitude?: number | null;
};

type Props = {
  initial?: Partial<AddressFormValue>;
  onSave: (value: AddressFormValue) => Promise<void> | void;
  onCancel: () => void;
  saving?: boolean;
};

export default function AddressForm({ initial, onSave, onCancel, saving }: Props) {
  const [form, setForm] = useState<AddressFormValue>({
    address_line1: initial?.address_line1 || '',
    address_line2: initial?.address_line2 || '',
    city: initial?.city || '',
    region: initial?.region || '',
    postal_code: initial?.postal_code || '',
    country_code: (initial?.country_code || 'GB').toUpperCase(),
    address_type: (initial?.address_type as any) || 'other',
    latitude: initial?.latitude ?? null,
    longitude: initial?.longitude ?? null,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitErr, setSubmitErr] = useState<string>('');

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.address_line1.trim()) e.address_line1 = 'Address line 1 is required';
    if (!form.city.trim()) e.city = 'City is required';
    if (!form.country_code.trim()) e.country_code = 'Country code is required';
    if (form.country_code && form.country_code.trim().length !== 2) e.country_code = 'Use 2-letter code (e.g., GB)';
    if (form.latitude != null && isNaN(Number(form.latitude))) e.latitude = 'Latitude must be a number';
    if (form.longitude != null && isNaN(Number(form.longitude))) e.longitude = 'Longitude must be a number';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    setSubmitErr('');
    if (!validate()) return;
    try {
      await onSave({
        ...form,
        country_code: form.country_code.toUpperCase(),
        latitude: form.latitude === null || form.latitude === undefined || form.latitude === ('' as unknown as number) ? null : Number(form.latitude),
        longitude: form.longitude === null || form.longitude === undefined || form.longitude === ('' as unknown as number) ? null : Number(form.longitude),
      });
    } catch (err) {
      setSubmitErr(err instanceof Error ? err.message : 'Failed to save');
    }
  };

  return (
    <div className="grid grid-cols-1 gap-3">
      {submitErr && <div className="text-sm text-red-600">{submitErr}</div>}
      <div>
        <Label>Address Line 1</Label>
        <Input value={form.address_line1} onChange={(e) => { setForm({ ...form, address_line1: e.target.value }); setErrors((p) => ({ ...p, address_line1: '' })); }} className={errors.address_line1 ? 'border-red-500' : ''} />
        {errors.address_line1 && <p className="text-xs text-red-600 mt-1">{errors.address_line1}</p>}
      </div>
      <div>
        <Label>Address Line 2</Label>
        <Input value={form.address_line2 || ''} onChange={(e) => setForm({ ...form, address_line2: e.target.value })} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label>City</Label>
          <Input value={form.city} onChange={(e) => { setForm({ ...form, city: e.target.value }); setErrors((p) => ({ ...p, city: '' })); }} className={errors.city ? 'border-red-500' : ''} />
          {errors.city && <p className="text-xs text-red-600 mt-1">{errors.city}</p>}
        </div>
        <div>
          <Label>Region</Label>
          <Input value={form.region || ''} onChange={(e) => setForm({ ...form, region: e.target.value })} />
        </div>
        <div>
          <Label>Postal Code</Label>
          <Input value={form.postal_code || ''} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label>Country</Label>
          <Input maxLength={2} value={form.country_code} onChange={(e) => { setForm({ ...form, country_code: e.target.value.toUpperCase() }); setErrors((p) => ({ ...p, country_code: '' })); }} className={errors.country_code ? 'border-red-500' : ''} />
          {errors.country_code && <p className="text-xs text-red-600 mt-1">{errors.country_code}</p>}
        </div>
        <div className="md:col-span-2">
          <Label>Type</Label>
          <Select value={form.address_type} onValueChange={(v) => setForm({ ...form, address_type: v as AddressFormValue['address_type'] })}>
            <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="warehouse">Warehouse</SelectItem>
              <SelectItem value="hub">Hub</SelectItem>
              <SelectItem value="customer_site">Customer Site</SelectItem>
              <SelectItem value="vendor">Vendor</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>Latitude (optional)</Label>
          <Input value={form.latitude ?? ''} onChange={(e) => { const v = e.target.value; setForm({ ...form, latitude: v === '' ? null : (v as unknown as number) }); setErrors((p) => ({ ...p, latitude: '' })); }} className={errors.latitude ? 'border-red-500' : ''} />
          {errors.latitude && <p className="text-xs text-red-600 mt-1">{errors.latitude}</p>}
        </div>
        <div>
          <Label>Longitude (optional)</Label>
          <Input value={form.longitude ?? ''} onChange={(e) => { const v = e.target.value; setForm({ ...form, longitude: v === '' ? null : (v as unknown as number) }); setErrors((p) => ({ ...p, longitude: '' })); }} className={errors.longitude ? 'border-red-500' : ''} />
          {errors.longitude && <p className="text-xs text-red-600 mt-1">{errors.longitude}</p>}
        </div>
      </div>

      <div className="flex items-center justify-between mt-2">
        <Button variant="secondary" type="button" onClick={onCancel}>Cancel</Button>
        <Button type="button" onClick={handleSave} disabled={saving}>Save</Button>
      </div>
    </div>
  );
}


